import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { BsPlusCircleFill } from "react-icons/bs";
import { CiEdit } from "react-icons/ci";
import { RiDeleteBin5Line, RiGalleryUploadLine } from "react-icons/ri";
import { LiaEyeSlashSolid, LiaEyeSolid } from "react-icons/lia";
import { ImUpload2 } from "react-icons/im";
import { RiUploadCloud2Fill } from "react-icons/ri";

import { GoUpload } from "react-icons/go";
import { FaBuilding, FaMapMarkerAlt, FaCalendarAlt, FaEllipsisV, FaHistory, FaCheckCircle, FaBan, FaUndo, FaUserCheck } from "react-icons/fa";
import MembershipAuditTrail from './MembershipAuditTrail';


interface UserStructure {
  profile: string;
  id: number;
  name: string;
  email: string;
  password?: string;
  plain_password?: string;
  delete: number;
  role:string;
  sub_role?: string;
  status?: string;
  payment_details?: string;
  designation:string;
  business_classification?: string;
  induction_by?: string;
  company_name:string;
  Time?: string;
  expire?: string;
  society_year?: string;
  point?: number;
}

interface MemberProps {
  filter?: string;
  onEditMember?: (id: number) => void;
}

const parseDate = (dateStr?: string) => {
  if (!dateStr) return null;
  const isoStr = dateStr.includes('T') ? dateStr : dateStr.replace(' ', 'T');
  const d = new Date(isoStr);
  return isNaN(d.getTime()) ? null : d;
};

const Member: React.FC<MemberProps> = ({ filter, onEditMember }) => {
  const [selectedImagePath, setSelectedImagePath] = useState<string | null>(null);
  const [societyYearFilter, setSocietyYearFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [showAuditTrailModal, setShowAuditTrailModal] = useState(false);
  const [selectedAuditUser, setSelectedAuditUser] = useState<UserStructure | null>(null);

  const getMembershipId = (user: UserStructure, allData: UserStructure[]) => {
    const d = parseDate(user.Time);
    const joinYear = d ? d.getFullYear() : 2026;
    const sameYearUsers = allData
      .filter(u => {
        const ud = parseDate(u.Time);
        const y = ud ? ud.getFullYear() : 2026;
        return y === joinYear;
      })
      .sort((a, b) => a.id - b.id);
    
    const index = sameYearUsers.findIndex(u => u.id === user.id);
    const seq = (index >= 0 ? index + 1 : 1).toString().padStart(2, '0');
    return `${joinYear}${seq}`;
  };

  const handleApproveMember = async (id: number) => {
    try {
      await axios.put(`https://rmbbackend.rmbjaipur.com/auth/member/${id}/convert-active`);
      alert('Member verified and activated successfully!');
      setNewData(prev => prev.map(m => m.id === id ? { ...m, status: 'active', role: 'Member', delete: 0, expire: '2027-06-30' } : m));
    } catch (error) {
      console.error('Error approving member:', error);
      alert('Failed to approve member.');
    }
  };

  const handleConvertToActive = async (id: number) => {
    try {
      await axios.put(`https://rmbbackend.rmbjaipur.com/auth/member/${id}/convert-active`);
      alert('🎉 Member successfully converted into an ACTIVE Member!');
      setNewData(prev => prev.map(m => m.id === id ? { ...m, status: 'active', role: 'Member', delete: 0, expire: '2027-06-30' } : m));
    } catch (error) {
      console.error('Error converting member to active:', error);
      alert('Failed to convert member to active status.');
    }
  };

  const handleToggleStatus = async (id: number, newStatus: string) => {
    try {
      await axios.put(`https://rmbbackend.rmbjaipur.com/auth/member/${id}/status`, { status: newStatus });
      alert(`Member status updated to ${newStatus}!`);
      setNewData(prev => prev.map(m => m.id === id ? { ...m, status: newStatus } : m));
    } catch (error) {
      console.error('Error updating member status:', error);
      alert('Failed to update member status.');
    }
  };

  const handleRestoreMember = async (id: number) => {
    try {
      await axios.put(`https://rmbbackend.rmbjaipur.com/auth/member/${id}/restore`);
      alert('Member restored successfully!');
      setNewData(prev => prev.filter(m => m.id !== id));
    } catch (error) {
      console.error('Error restoring member:', error);
      alert('Failed to restore member.');
    }
  };

  const handleCopyRegistrationLink = () => {
    const link = `${window.location.origin}/register`;
    navigator.clipboard.writeText(link);
    alert('Registration link copied to clipboard!');
  };

  const handleImageSelect = (path: string) => {
    setSelectedImagePath(path); // Save the selected path in the parent component
    console.log("Selected Image Path:", path);
  };

  const closegallery = () => {
    setGallery(false);
  };

  const navigate = useNavigate();
  const [searchNotFound, setSearchNotFound] = useState<string>("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [newData, setNewData] = useState<UserStructure[]>([]);
  const [gallery, setGallery] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const handleSearch = async () => {
    console.log("Searching for:", searchQuery);

    try {
      // Make sure the searchQuery is not empty before making the request
      if (!searchQuery) {
        alert("Please enter a search term");
        return;
      }

      // Sending a GET request to the backend with the search query
      const response = await axios.get(
        `https://rmbbackend.rmbjaipur.com/auth/member/search/${searchQuery}`
      );
      console.log("user data", response.data);
      setNewData(response.data);

      // Log the response data for debugging
      console.log("Search results:", response.data);
      setSearchNotFound("");
      // Handle the data (e.g., updating state, displaying results)
      // For example, you could update the state here
      // setSearchResults(response.data);
    } catch (error) {
      setSearchNotFound("Not Found");
      console.error("Error during search:", error);
    }
  };

  // Open modal
  const openModal = (postId: number) => {
    setSelectedUserId(postId);
    setIsModalOpen(true);
  };

  // Function to close modal and clear the post ID
  const closeModal = () => {
    setSelectedUserId(null);
    setIsModalOpen(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('authToken') || '';
        const headers: Record<string, string> = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const response = await axios.get<UserStructure[]>(
          "https://rmbbackend.rmbjaipur.com/auth/allusersinfo",
          { headers, withCredentials: true }
        );

        let fetchedData = response.data;
        
        // Society Year Dropdown Filter: Shows all active members in the selected society year
        if (societyYearFilter !== "All" && statusFilter !== "inactive" && statusFilter !== "deleted") {
          const [startYear, endYear] = societyYearFilter.split("-");
          const startDate = new Date(`${startYear}-07-01T00:00:00`);
          const endDate = new Date(`${endYear}-06-30T23:59:59`);
          fetchedData = fetchedData.filter(user => {
            if (user.role === 'super-admin') return false;
            const joinDate = parseDate(user.Time);
            if (joinDate && joinDate > endDate) return false; // Joined after this society year ended

            const expireDate = user.expire ? parseDate(user.expire) : null;
            if (expireDate && expireDate < startDate) return false; // Expired before this society year started

            return true;
          });
        }

        // Dashboard Metric Filters (active, new, grace, renewals)
        if (filter) {
          const now = new Date();
          const month = now.getMonth();
          const date = now.getDate();

          fetchedData = fetchedData.filter(user => {
            const joinDate = parseDate(user.Time);
            if (!joinDate) return false;
            const diffDaysFromJoin = (now.getTime() - joinDate.getTime()) / (1000 * 3600 * 24);

            if (filter === 'active') {
               return user.role !== 'super-admin' && user.status === 'active';
            } else if (filter === 'new') {
               return user.role !== 'super-admin' && diffDaysFromJoin <= 30;
            } else if (filter === 'grace') {
               // Grace Period: Membership expiry is in the next 45 days (320 to 365 days since join)
               return user.role !== 'super-admin' && user.delete !== 1 && diffDaysFromJoin >= 320 && diffDaysFromJoin <= 365;
            } else if (filter === 'renewals') {
               const inRenewalPeriod = month === 5; // June 1 - June 30
               return user.role !== 'super-admin' && inRenewalPeriod;
            }
            return true;
          });
        }
        // Filter out deleted members by default unless explicitly viewing Deleted Members
        if (statusFilter === "deleted") {
          fetchedData = fetchedData.filter(user => user.delete === 1);
        } else if (statusFilter === "grace") {
          const now = new Date();
          fetchedData = fetchedData.filter(user => {
            if (user.delete === 1 || user.role === 'super-admin') return false;
            const joinDate = parseDate(user.Time);
            if (!joinDate) return false;
            const diffDaysFromJoin = (now.getTime() - joinDate.getTime()) / (1000 * 3600 * 24);
            return diffDaysFromJoin >= 320 && diffDaysFromJoin <= 365;
          });
        } else if (statusFilter === "expired") {
          const now = new Date();
          fetchedData = fetchedData.filter(user => {
            if (user.delete === 1 || user.role === 'super-admin') return false;
            const joinDate = parseDate(user.Time);
            if (!joinDate) return false;
            const diffDaysFromJoin = (now.getTime() - joinDate.getTime()) / (1000 * 3600 * 24);
            return diffDaysFromJoin > 365 || user.status === 'inactive';
          });
        } else {
          fetchedData = fetchedData.filter(user => user.delete !== 1);
          if (statusFilter !== "All") {
            fetchedData = fetchedData.filter(user => (user.status || 'active') === statusFilter);
          }
        }

        setNewData(fetchedData);
        console.log("user data in a array", fetchedData);
      } catch (error) {
        console.error("user Not Found:", error);
      }
    };

    fetchData();
  }, [filter, societyYearFilter, statusFilter]);

  function deletePost(id: number): void {
    const confirmation = window.confirm(
      "Are you sure you want to delete this member?"
    );

    if (confirmation) {
      axios
        .delete(`https://rmbbackend.rmbjaipur.com/auth/member/${id}`)
        .then((response) => {
          // Handle the success case
          alert("Member deleted successfully");
          console.log(response.data);
          setNewData(prev => prev.filter(item => item.id !== id));
        })
        .catch((error) => {
          // Handle errors
          console.error("Error deleting post:", error);
          alert("Failed to delete the post. Please try again.");
        });
    }
  }

  const [topStats, setTopStats] = useState({
    total_members: 0,
    active_members: 0,
    grace_period: 0,
    expired: 0
  });

  useEffect(() => {
    const fetchTopStats = async () => {
      try {
        const res = await axios.get("https://rmbbackend.rmbjaipur.com/auth/dashboard-stats");
        if (res.data) {
          setTopStats({
            total_members: res.data.total_members || 0,
            active_members: res.data.active_members !== undefined ? res.data.active_members : (res.data.total_members || 0),
            grace_period: res.data.grace_period || 0,
            expired: res.data.expired || 0
          });
        }
      } catch (err) {
        console.error("Error fetching top stats:", err);
      }
    };
    fetchTopStats();
  }, [newData.length]);

  return (
    <div className="p-2 sm:p-4 max-w-full overflow-x-hidden space-y-4">
      {/* Top Counter Bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 sm:gap-3">
        <div 
          onClick={() => setStatusFilter("All")}
          className={`p-3 rounded-2xl border cursor-pointer transition-all shadow-sm flex items-center justify-between ${
            statusFilter === "All" ? "bg-blue-600 text-white border-blue-700 shadow-md" : "bg-white text-gray-800 border-gray-200 hover:border-blue-300"
          }`}
        >
          <div>
            <div className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider opacity-80">👥 Total Roster</div>
            <div className="text-xl sm:text-2xl font-black mt-0.5">{topStats.total_members}</div>
          </div>
          <span className="text-lg sm:text-xl">📊</span>
        </div>

        <div 
          onClick={() => setStatusFilter("active")}
          className={`p-3 rounded-2xl border cursor-pointer transition-all shadow-sm flex items-center justify-between ${
            statusFilter === "active" ? "bg-emerald-600 text-white border-emerald-700 shadow-md" : "bg-white text-gray-800 border-gray-200 hover:border-emerald-300"
          }`}
        >
          <div>
            <div className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider opacity-80">🟢 Active Members</div>
            <div className="text-xl sm:text-2xl font-black mt-0.5">{topStats.active_members}</div>
          </div>
          <span className="text-lg sm:text-xl">✅</span>
        </div>

        <div 
          onClick={() => setStatusFilter("grace")}
          className={`p-3 rounded-2xl border cursor-pointer transition-all shadow-sm flex items-center justify-between ${
            statusFilter === "grace" ? "bg-amber-600 text-white border-amber-700 shadow-md" : "bg-white text-gray-800 border-gray-200 hover:border-amber-300"
          }`}
        >
          <div>
            <div className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider opacity-80">⚠️ Grace (45d)</div>
            <div className="text-xl sm:text-2xl font-black mt-0.5">{topStats.grace_period}</div>
          </div>
          <span className="text-lg sm:text-xl">⏳</span>
        </div>

        <div 
          onClick={() => setStatusFilter("expired")}
          className={`p-3 rounded-2xl border cursor-pointer transition-all shadow-sm flex items-center justify-between ${
            statusFilter === "expired" ? "bg-rose-600 text-white border-rose-700 shadow-md" : "bg-white text-gray-800 border-gray-200 hover:border-rose-300"
          }`}
        >
          <div>
            <div className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider opacity-80">🛑 Expired</div>
            <div className="text-xl sm:text-2xl font-black mt-0.5">{topStats.expired}</div>
          </div>
          <span className="text-lg sm:text-xl">💳</span>
        </div>
      </div>

      {/* Control & Search Bar */}
      <div className="flex flex-col lg:flex-row justify-between items-stretch lg:items-center p-3.5 bg-white dark:bg-[#1E2B45] rounded-2xl border border-gray-200 dark:border-[#2A3B5C] shadow-sm gap-3">
        <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto">
          <button
            className="flex-1 sm:flex-none justify-center h-[38px] px-3 bg-[#FDBC12] text-darkblue items-center rounded-xl gap-1.5 shadow font-black text-xs hover:bg-amber-400 transition cursor-pointer active:scale-95 flex"
            onClick={() => openModal(-1)}
          >
            <BsPlusCircleFill size={14} /> <span>Add New</span>
          </button>
          
          <button
            className="flex-1 sm:flex-none justify-center h-[38px] px-3 bg-[#02256D] text-white font-bold items-center rounded-xl gap-1.5 shadow hover:bg-[#03369B] transition text-xs cursor-pointer active:scale-95 flex"
            onClick={() => {
              setSelectedAuditUser(null);
              setShowAuditTrailModal(true);
            }}
          >
            <span>📜 Audit Trail</span>
          </button>

          <button
            className="flex-1 sm:flex-none justify-center h-[38px] px-3 bg-[#FDBC12] text-darkblue font-bold items-center rounded-xl gap-1.5 shadow text-xs hover:bg-amber-400 transition cursor-pointer active:scale-95 flex"
            onClick={handleCopyRegistrationLink}
          >
            <span>🔗 Copy Reg Link</span>
          </button>

          <button
            className="flex-1 sm:flex-none justify-center h-[38px] px-3 bg-emerald-700 text-white font-bold items-center rounded-xl gap-1.5 shadow text-xs hover:bg-emerald-600 transition cursor-pointer active:scale-95 flex"
            onClick={() => {
              const link = `${window.location.origin}/visitor-register`;
              navigator.clipboard.writeText(link);
              alert('🎉 Visitor Registration link copied to clipboard! Share this link with prospective visitors.');
            }}
            title="Copy shareable link for Visitor Registration Form"
          >
            <span>🎟️ Visitor Reg Link</span>
          </button>

          <select 
            className="flex-1 sm:flex-none h-[38px] px-2.5 bg-slate-50 dark:bg-slate-800 text-darkblue dark:text-slate-100 border border-gray-300 dark:border-slate-700 rounded-xl outline-none text-xs font-bold"
            value={societyYearFilter}
            onChange={(e) => setSocietyYearFilter(e.target.value)}
          >
            <option value="All">All Years</option>
            <option value="2023-2024">2023-2024</option>
            <option value="2024-2025">2024-2025</option>
            <option value="2025-2026">2025-2026</option>
            <option value="2026-2027">2026-2027</option>
          </select>

          <select 
            className="flex-1 sm:flex-none h-[38px] px-2.5 bg-slate-50 dark:bg-slate-800 text-darkblue dark:text-slate-100 border border-gray-300 dark:border-slate-700 rounded-xl outline-none font-bold text-xs"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="All">All Statuses</option>
            <option value="active">Active Members</option>
            <option value="grace">⚠️ Grace Period (Next 45 Days)</option>
            <option value="expired">🛑 Expired / Not Renewed</option>
            <option value="pending">Pending Verification (Self-Reg)</option>
            <option value="inactive">Inactive Members</option>
            <option value="deleted">🗑️ Deleted Members (Trash)</option>
          </select>
        </div>

        <div className="w-full lg:w-72">
          <div className="bg-slate-50 dark:bg-slate-800 border h-[38px] border-slate-300 dark:border-slate-700 px-3 rounded-xl flex justify-between items-center text-xs">
            <input
              type="search"
              name="search"
              className="placeholder-slate-400 border-none outline-none bg-transparent w-full text-slate-800 dark:text-white font-bold"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search member or company..."
            />
            <button
              className="cursor-pointer px-2 border-l border-slate-300 dark:border-slate-700 h-full flex items-center justify-center font-black text-amber-500"
              onClick={handleSearch}
            >
              Search
            </button>
          </div>
          {searchNotFound && <p className="text-red-500 text-xs mt-1 font-bold">{searchNotFound}</p>}
        </div>
      </div>

      <div>
        {isModalOpen && (
          <div className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm z-50 p-3">
            <div className="bg-white dark:bg-[#1E2B45] rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl border border-slate-200 dark:border-[#2A3B5C]">
              <Modal userId={selectedUserId} onClose={closeModal} />
            </div>
          </div>
        )}
        <div className="bg-white dark:bg-[#1E2B45] rounded-2xl shadow-sm border border-slate-200 dark:border-[#2A3B5C] overflow-hidden">
          <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-230px)] no-scrollbar relative">
            <table className="table-auto w-full border-collapse">
              <thead className="text-white text-xs uppercase tracking-wider font-semibold sticky top-0 z-20 shadow-md">
                <tr>
                  <th className="bg-[#0A2451] dark:bg-[#15233E] px-3 py-3.5 text-left font-bold w-12 border-b border-[#1E3A70] sticky top-0">S.No. ↑</th>
                  <th className="bg-[#0A2451] dark:bg-[#15233E] px-3 py-3.5 text-left font-bold border-b border-[#1E3A70] sticky top-0">Member Details ↑</th>
                  <th className="bg-[#0A2451] dark:bg-[#15233E] px-3 py-3.5 text-left font-bold whitespace-nowrap border-b border-[#1E3A70] sticky top-0">Date of Joining ↑</th>
                  <th className="bg-[#0A2451] dark:bg-[#15233E] px-3 py-3.5 text-left font-bold whitespace-nowrap border-b border-[#1E3A70] sticky top-0">Renewal Date ↑</th>
                  <th className="bg-[#0A2451] dark:bg-[#15233E] px-3 py-3.5 text-left font-bold whitespace-nowrap border-b border-[#1E3A70] sticky top-0">Updated Date ↑</th>
                  <th className="bg-[#0A2451] dark:bg-[#15233E] px-3 py-3.5 text-center font-bold w-20 border-b border-[#1E3A70] sticky top-0">Actions</th>
                </tr>
              </thead>
  <tbody>
    {(() => {
      const filteredList = newData.filter(item => {
        if (!searchQuery.trim()) return true;
        const q = searchQuery.toLowerCase();
        return (
          item.name?.toLowerCase().includes(q) ||
          item.company_name?.toLowerCase().includes(q) ||
          item.business_classification?.toLowerCase().includes(q) ||
          item.designation?.toLowerCase().includes(q) ||
          item.induction_by?.toLowerCase().includes(q) ||
          item.role?.toLowerCase().includes(q) ||
          item.email?.toLowerCase().includes(q)
        );
      });

      if (filteredList.length === 0) {
        return (
          <tr>
            <td colSpan={6} className="text-center py-8 text-gray-500 font-medium">
              No members found matching "{searchQuery}"
            </td>
          </tr>
        );
      }

      return filteredList.map((item, index) => {
        const d = parseDate(item.Time);
        const dateString = d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Jun 15, 2026';
        
        // Renewal Date: Defaults to 30 June unless specified by Admin
        let renewalDateStr = `Jun 30, 2026`;
        if (item.expire) {
          const expD = parseDate(item.expire);
          if (expD) {
            renewalDateStr = expD.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
          }
        } else if (d) {
          const nextYr = Math.max(d.getFullYear() + 1, new Date().getFullYear() + 1);
          renewalDateStr = `Jun 30, ${nextYr}`;
        }

        return (
          <tr key={item.id} className="border-b border-gray-200 dark:border-[#2A3B5C] hover:bg-gray-50 dark:hover:bg-slate-800/60 text-[13px] text-slate-800 dark:text-slate-200">
            <td className="px-3 py-3 align-top text-gray-700 dark:text-slate-300 font-bold">{index + 1}</td>
            <td className="px-3 py-3 align-top">
              <div className="flex gap-3">
                <img
                  className="h-[60px] object-cover w-[60px] rounded-full mt-1 border border-slate-200 dark:border-slate-700 shadow-sm flex-shrink-0"
                  src={
                    item.profile
                      ? item.profile.startsWith("http")
                        ? item.profile
                        : `https://rmbbackend.rmbjaipur.com/${item.profile.replace(/\\/g, '/')}`
                      : "https://via.placeholder.com/150"
                  }
                  alt={item.name}
                />
                <div>
                  <div className="font-extrabold text-[#0A2451] dark:text-amber-400 text-sm">{item.name}</div>
                  <div className="font-bold text-[#2f5597] dark:text-blue-400 mb-1">Membership Id: <span className="font-normal text-gray-700 dark:text-slate-300">{getMembershipId(item, newData)}</span></div>
                  <div className="font-bold text-gray-800 dark:text-slate-200 mb-1 flex items-center gap-1.5 flex-wrap">
                    <span className="text-xs font-bold text-gray-700 dark:text-slate-300">Total Points:</span>
                    <span className="bg-[#FDBC12] text-[#0A2451] text-xs font-black px-2 py-0.5 rounded-full border border-amber-400 shadow-sm flex items-center gap-1">
                      🏆 {item.point || 0} Pts
                    </span>
                    <span className="bg-emerald-100 dark:bg-emerald-950/70 text-emerald-900 dark:text-emerald-300 text-xs font-black px-2 py-0.5 rounded-full border border-emerald-300 dark:border-emerald-700 shadow-sm flex items-center gap-1">
                      🏷️ {item.society_year || '2025-2026'}
                    </span>
                  </div>
                  <div className="font-bold text-gray-800 dark:text-slate-200 mb-1">Role: <span className="font-semibold text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/60 px-1.5 py-0.5 rounded border border-blue-200 dark:border-blue-800 text-xs">{item.role || 'Member'}{item.sub_role ? ` • ${item.sub_role}` : ''}</span></div>
                  <div className="flex items-center gap-1 text-gray-600 dark:text-slate-400 mb-0.5"><FaBuilding className="text-gray-500 dark:text-slate-400 text-[11px]" /> <span className="font-bold text-gray-700 dark:text-slate-300">Company:</span> <span className="text-slate-800 dark:text-slate-200 font-semibold">{item.company_name || 'N/A'}</span></div>
                  <div className="flex items-center gap-1 text-gray-600 dark:text-slate-400 mb-0.5"><span className="font-bold text-gray-700 dark:text-slate-300">Business Classification:</span> <span className="text-slate-800 dark:text-slate-200 font-semibold">{item.business_classification || item.designation || 'N/A'}</span></div>
                  <div className="flex items-center gap-1 text-gray-600 dark:text-slate-400 mb-0.5"><span className="font-bold text-gray-700 dark:text-slate-300">Designation:</span> <span className="text-slate-800 dark:text-slate-200 font-semibold">{item.designation || 'N/A'}</span></div>
                  {item.induction_by && (
                    <div className="inline-flex items-center gap-1 text-[#0A2451] dark:text-amber-300 mb-1 font-bold text-xs bg-amber-50 dark:bg-amber-950/60 px-2 py-0.5 rounded border border-amber-200 dark:border-amber-800 mt-0.5">
                      <span>🤝 Inducted By:</span> <span className="text-amber-900 dark:text-amber-200 font-extrabold">{item.induction_by}</span>
                    </div>
                  )}
                  {item.payment_details && (
                    <div className="text-xs bg-amber-50 dark:bg-amber-950/60 text-amber-900 dark:text-amber-300 border border-amber-200 dark:border-amber-800 rounded px-1.5 py-0.5 mt-1 font-medium">
                      💳 <b>Payment:</b> {item.payment_details}
                    </div>
                  )}
                  <div className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-slate-400 mb-0.5 mt-0.5">
                    <span className="font-bold text-gray-700 dark:text-slate-300">🔑 Password:</span>
                    <span className="text-amber-800 dark:text-amber-300 font-mono font-bold bg-amber-50 dark:bg-amber-950/60 px-1.5 py-0.5 rounded border border-amber-200 dark:border-amber-800/60 text-[11px]">
                      {item.plain_password || item.password || 'N/A'}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 mt-1">
                    <span className="font-bold text-xs text-gray-700 dark:text-slate-300">Status:</span>
                    <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full uppercase ${
                      item.delete === 1 ? 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border border-gray-400' :
                      item.status === 'active' ? 'bg-green-100 dark:bg-green-950/70 text-green-800 dark:text-green-300 border border-green-300 dark:border-green-800' :
                      item.status === 'pending' ? 'bg-yellow-100 dark:bg-yellow-950/70 text-yellow-800 dark:text-yellow-300 border border-yellow-300 dark:border-yellow-800' :
                      'bg-red-100 dark:bg-red-950/70 text-red-800 dark:text-red-300 border border-red-300 dark:border-red-800'
                    }`}>
                      {item.delete === 1 ? 'DELETED (TRASH)' : (item.status || 'active')}
                    </span>
                  </div>
                </div>
              </div>
            </td>
            <td className="px-2 py-2 align-top text-gray-600 dark:text-slate-300 font-medium">{dateString}</td>
            <td className="px-2 py-2 align-top text-emerald-700 dark:text-emerald-400 font-bold bg-emerald-50/50 dark:bg-emerald-950/40 rounded">{renewalDateStr}</td>
            <td className="px-2 py-2 align-top text-gray-600 dark:text-slate-300 font-medium">{dateString}</td>
            <td className="px-2 py-2 align-top text-center whitespace-nowrap">
              <div className="flex items-center justify-center gap-1.5 py-1">
                {/* 📜 Audit Ledger Icon Button */}
                <button
                  className="p-2 bg-[#02256D] hover:bg-[#03369B] text-[#FDBC12] rounded-lg shadow-sm transition flex items-center justify-center"
                  onClick={() => {
                    setSelectedAuditUser(item);
                    setShowAuditTrailModal(true);
                  }}
                  title={`View Audit Ledger for ${item.name}`}
                >
                  <FaHistory size={14} />
                </button>

                {/* ✏️ Edit Profile Icon Button */}
                <button
                  className="p-2 bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 rounded-lg shadow-sm transition flex items-center justify-center"
                  onClick={() => {
                    if (onEditMember) {
                      onEditMember(item.id);
                    } else {
                      openModal(item.id);
                    }
                  }}
                  title="Edit Member Profile & Credentials"
                >
                  <CiEdit size={16} />
                </button>

                {/* 🎭 Impersonate Member (Login As User) Icon Button */}
                {item.role !== 'super-admin' && item.name?.toLowerCase() !== 'admin' && (
                  <button
                    className="p-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg shadow-sm transition flex items-center justify-center cursor-pointer"
                    onClick={async () => {
                      if (window.confirm(`Are you sure you want to login as member "${item.name}" to perform activities on their behalf?`)) {
                        try {
                          const res = await axios.post('https://rmbbackend.rmbjaipur.com/auth/impersonate', { user_id: item.id }, { withCredentials: true });
                          if (res.data.success) {
                            localStorage.setItem('isImpersonating', 'true');
                            localStorage.setItem('impersonatedUser', JSON.stringify(res.data.user));
                            window.location.href = '/memberportal';
                          } else {
                            alert(res.data.error || 'Failed to impersonate member.');
                          }
                        } catch (err) {
                          console.error('Error impersonating member:', err);
                          alert('Failed to impersonate member.');
                        }
                      }
                    }}
                    title={`Login as ${item.name} (Impersonate Member)`}
                  >
                    <FaUserCheck size={14} />
                  </button>
                )}

                {/* ⚡ Convert to Active Member Button */}
                {item.status !== 'active' && item.delete !== 1 && (
                  <button
                    className="p-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg shadow-sm transition flex items-center justify-center cursor-pointer"
                    onClick={() => handleConvertToActive(item.id)}
                    title={`Convert ${item.name} into an ACTIVE Member`}
                  >
                    <FaUserCheck size={14} />
                  </button>
                )}

                {/* Status Toggle / Approve Icon Button */}
                {item.delete !== 1 && item.status === 'pending' && (
                  <button
                    className="p-2 bg-emerald-100 text-emerald-800 hover:bg-emerald-200 border border-emerald-300 rounded-lg shadow-sm transition flex items-center justify-center"
                    onClick={() => handleApproveMember(item.id)}
                    title="Approve & Activate Member Account"
                  >
                    <FaCheckCircle size={14} />
                  </button>
                )}

                {item.delete !== 1 && (item.status === 'active' || !item.status) && (
                  <button
                    className="p-2 bg-amber-50 text-amber-800 hover:bg-amber-100 border border-amber-300 rounded-lg shadow-sm transition flex items-center justify-center"
                    onClick={() => handleToggleStatus(item.id, 'inactive')}
                    title="Make Member Inactive"
                  >
                    <FaBan size={14} />
                  </button>
                )}

                {item.delete !== 1 && item.status === 'inactive' && (
                  <button
                    className="p-2 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 border border-emerald-300 rounded-lg shadow-sm transition flex items-center justify-center"
                    onClick={() => handleToggleStatus(item.id, 'active')}
                    title="Make Member Active"
                  >
                    <FaCheckCircle size={14} />
                  </button>
                )}

                {/* Delete / Restore Icon Button */}
                {item.delete === 1 ? (
                  <button
                    className="p-2 bg-cyan-50 text-cyan-800 hover:bg-cyan-100 border border-cyan-300 rounded-lg shadow-sm transition flex items-center justify-center"
                    onClick={() => handleRestoreMember(item.id)}
                    title="Restore Member from Trash"
                  >
                    <FaUndo size={14} />
                  </button>
                ) : (
                  <button
                    className="p-2 bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 rounded-lg shadow-sm transition flex items-center justify-center"
                    onClick={() => deletePost(item.id)}
                    title="Move Member to Trash"
                  >
                    <RiDeleteBin5Line size={16} />
                  </button>
                )}
              </div>
            </td>
           </tr>
         );
      });
    })()}
  </tbody>
</table>
</div>
</div>
</div>

      {/* Membership Audit Trail Modal */}
      {showAuditTrailModal && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto p-6 relative shadow-2xl">
            <button
              onClick={() => setShowAuditTrailModal(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-900 font-black text-xl w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center shadow z-10"
            >
              ✕
            </button>
            <MembershipAuditTrail selectedUserId={selectedAuditUser?.id} selectedUserName={selectedAuditUser?.name} />
          </div>
        </div>
      )}
    </div>
  );
};

interface ModalProps {
  userId?: number | null;
  onClose: () => void;
}

const Modal: React.FC<ModalProps> = ({ userId, onClose }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false); // State for dropdown menu visibility

  const dropdownRef = useRef<HTMLDivElement | null>(null); // Type the ref here
  // Reference for the dropdown menu

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const [selectedImagePath, setSelectedImagePath] = useState<string | null>(
    null
  );

  const handleImageSelect = (path: string) => {
    setSelectedImagePath(path); // Save the selected path in the parent component
    // setFormData((prev) => ({ ...prev, image:  }));
    setFileName(path);
    console.log("Selected Image Path:", path);
  };

  const closegallery = () => {
    setGallery(false);
  };

  const [gallery, setGallery] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [availableRolesList, setAvailableRolesList] = useState<any[]>([]);
  const [activeMembersList, setActiveMembersList] = useState<any[]>([]);

  useEffect(() => {
    Promise.all([
      axios.get('https://rmbbackend.rmbjaipur.com/auth/roles').catch(() => ({ data: { roles: [] } })),
      axios.get('https://rmbbackend.rmbjaipur.com/auth/allusersinfo').catch(() => ({ data: [] }))
    ]).then(([rolesRes, membersRes]) => {
      if (rolesRes.data && rolesRes.data.roles) {
        setAvailableRolesList(rolesRes.data.roles);
      }
      if (membersRes.data) {
        setActiveMembersList(Array.isArray(membersRes.data) ? membersRes.data : []);
      }
    });
  }, []);

  const formatDate = (isoDate: string): string => {
    const date = new Date(isoDate);
    return date.toISOString().split("T")[0]; // Extracts yyyy-MM-dd
  };

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    number: "",
    DOB: "",
    role: "",
    sub_role: "",
    company_name: "",
    business_classification: "",
    designation: "",
    induction_by: "",
    company_address: "",
    company_website: "",
    about_company: "",
    social: "",
    image: null as File | null,
    password: "",
    status: "active",
    payment_details: ""
  });
  const [fileName, setFileName] = useState<string | null>(null);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (userId != -1) {
      const fetchPost = async () => {
        try {
          const response = await axios.get(
            `https://rmbbackend.rmbjaipur.com/auth/user/${userId}`
          );

          const data = Array.isArray(response.data)
            ? response.data[0]
            : response.data;
          // Access the first element

          const {
            name, email, number, DOB, role, company_name, designation, business_classification,
            company_address, company_website, about_company, social,
            profile: image, password, plain_password, status, payment_details, sub_role, induction_by
          } = data;

          setFormData({
            name: name || "",
            email: email || "",
            number: number || "",
            DOB: DOB ? formatDate(DOB) : "",
            role: role || "member",
            sub_role: sub_role || "",
            company_name: company_name || "",
            business_classification: business_classification || designation || "",
            designation: designation || "",
            induction_by: induction_by || "",
            company_address: company_address || "",
            company_website: company_website || "",
            about_company: about_company || "",
            social: social || "",
            password: plain_password || password || "",
            image: image || null,
            status: status || "active",
            payment_details: payment_details || ""
          });

          setFileName(image);
        } catch (err) {
          console.error("Error fetching users:", err);
        }
      };
      fetchPost();
    }
  }, [userId]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormData((prev) => ({ ...prev, image: file }));
    setFileName(file ? file.name : null);
    setSelectedImagePath(file ? file.name : null);
  };

  const closeGallery = () => {
    setGallery(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate fields
    if (!formData.name || !formData.password || !formData.email) {
      setError("All fields are required.");
      return;
    }

    const data = new FormData();
    data.append("name", formData.name);
    data.append("email", formData.email);
    data.append("number", formData.number);
    data.append("DOB", formData.DOB);
    data.append("role", formData.role);
    data.append("sub_role", formData.sub_role);
    data.append("company_name", formData.company_name);
    data.append("business_classification", formData.business_classification);
    data.append("designation", formData.designation);
    data.append("induction_by", formData.induction_by);
    data.append("company_address", formData.company_address);
    data.append("company_website", formData.company_website);
    data.append("about_company", formData.about_company);
    data.append("social", formData.social);
    data.append("password", formData.password);
    data.append("status", formData.status);
    data.append("payment_details", formData.payment_details);
    data.append("company_address", formData.company_address);
    data.append("company_website", formData.company_website);
    data.append("about_company", formData.about_company);
    data.append("social", formData.social);
    data.append("password", formData.password);
    data.append("status", formData.status);
    data.append("payment_details", formData.payment_details);
    if (formData.image) {
      data.append("image", formData.image);
    } else if (fileName) {
      data.append("image", fileName);
    }

    try {
      if (userId != -1) {
        await axios.put(`https://rmbbackend.rmbjaipur.com/auth/member/${userId}`, data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        console.log("Post updated successfully");
      } else {
        await axios.post("https://rmbbackend.rmbjaipur.com/auth/member", data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        console.log("Post created successfully");
      }
      onClose();
      window.location.href = "/dashboard";
    } catch (err) {
      console.error("Error submitting post:", err);
      setError("Failed to submit the post. Please try again.");
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-3xl max-h-[90vh] p-6 rounded-xl shadow-2xl relative overflow-y-auto">
        <h2 className="text-xl font-bold text-gray-800 border-b pb-3 mb-4 flex justify-between items-center">
          <span>{userId !== -1 ? '✏️ Edit Member Information' : '➕ Add New Member'}</span>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 font-bold text-lg">✕</button>
        </h2>

        {error && <div className="mb-4 text-xs font-bold text-red-600 bg-red-50 p-2.5 rounded-lg border border-red-200">{error}</div>}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Section 1: Personal Details */}
          <div className="bg-blue-50/50 p-4 rounded-xl border border-blue-100">
            <h3 className="text-xs font-bold text-[#0A2451] uppercase mb-3 tracking-wider">👤 Personal Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-gray-700">Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter full name"
                  className="mt-1 w-full p-2.5 bg-white border border-gray-300 rounded-lg outline-none text-sm focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-700">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter email address"
                  className="mt-1 w-full p-2.5 bg-white border border-gray-300 rounded-lg outline-none text-sm focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-700">Contact / Mobile Number</label>
                <input
                  type="text"
                  name="number"
                  value={formData.number}
                  onChange={handleChange}
                  placeholder="e.g. +91 98290 12345"
                  className="mt-1 w-full p-2.5 bg-white border border-gray-300 rounded-lg outline-none text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-700">Date of Birth (DOB)</label>
                <input
                  type="date"
                  name="DOB"
                  value={formData.DOB}
                  onChange={handleChange}
                  className="mt-1 w-full p-2.5 bg-white border border-gray-300 rounded-lg outline-none text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Profile Picture Upload & Preview */}
              <div className="md:col-span-2 bg-white p-3.5 rounded-lg border border-blue-200 shadow-sm mt-1">
                <label className="text-xs font-bold text-[#0A2451] block mb-2 flex items-center justify-between">
                  <span>🖼️ Member Profile Picture / Avatar</span>
                  <span className="text-[11px] text-gray-500 font-normal">Select image to update member avatar</span>
                </label>
                <div className="flex flex-col sm:flex-row items-center gap-4">
                  <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-[#FDBC12] shadow flex-shrink-0 bg-gray-100 flex items-center justify-center">
                    {formData.image ? (
                      <img
                        src={
                          typeof formData.image === 'string'
                            ? (String(formData.image).startsWith('http') ? String(formData.image) : `https://rmbbackend.rmbjaipur.com/${String(formData.image).replace(/\\/g, '/')}`)
                            : URL.createObjectURL(formData.image as File)
                        }
                        alt="Profile Preview"
                        className="w-full h-full object-cover rounded-full"
                        onError={(e) => { e.currentTarget.src = 'https://via.placeholder.com/150?text=User'; }}
                      />
                    ) : (
                      <span className="text-xs text-gray-400 font-bold">No Photo</span>
                    )}
                  </div>
                  <div className="w-full space-y-1.5">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="w-full text-xs text-gray-600 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-[#02256D] file:text-white hover:file:bg-[#03369B] cursor-pointer"
                    />
                    {fileName && typeof fileName === 'string' && (
                      <p className="text-[11px] text-emerald-700 font-mono font-semibold truncate">
                        Current Image File: {fileName}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 2: Business & Professional Details */}
          <div className="bg-amber-50/50 p-4 rounded-xl border border-amber-100">
            <h3 className="text-xs font-bold text-amber-900 uppercase mb-3 tracking-wider">🏢 Business & Professional Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-gray-700">Company / Firm Name</label>
                <input
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  placeholder="Enter company name"
                  className="mt-1 w-full p-2.5 bg-white border border-gray-300 rounded-lg outline-none text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-700">Business Classification</label>
                <input
                  type="text"
                  name="business_classification"
                  value={formData.business_classification}
                  onChange={handleChange}
                  placeholder="e.g. IT Services, Real Estate, Healthcare"
                  className="mt-1 w-full p-2.5 bg-white border border-gray-300 rounded-lg outline-none text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-700">Designation</label>
                <input
                  type="text"
                  name="designation"
                  value={formData.designation}
                  onChange={handleChange}
                  placeholder="e.g. Managing Director, Founder, CEO"
                  className="mt-1 w-full p-2.5 bg-white border border-gray-300 rounded-lg outline-none text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-700">Induction By (Inducted Member)</label>
                <select
                  name="induction_by"
                  value={formData.induction_by}
                  onChange={handleChange}
                  className="mt-1 w-full p-2.5 bg-white border border-gray-300 rounded-lg outline-none text-sm font-bold text-blue-900 focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">-- Select Inducting Member --</option>
                  {activeMembersList.map(m => (
                    <option key={m.id} value={m.name}>{m.name} ({m.company_name || 'Member'})</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-700">Company Website</label>
                <input
                  type="text"
                  name="company_website"
                  value={formData.company_website}
                  onChange={handleChange}
                  placeholder="e.g. https://mycompany.com"
                  className="mt-1 w-full p-2.5 bg-white border border-gray-300 rounded-lg outline-none text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-700">Social Media Links</label>
                <input
                  type="text"
                  name="social"
                  value={formData.social}
                  onChange={handleChange}
                  placeholder="LinkedIn, Twitter, Facebook handles"
                  className="mt-1 w-full p-2.5 bg-white border border-gray-300 rounded-lg outline-none text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="md:col-span-2">
                <label className="text-xs font-semibold text-gray-700">Office / Company Address</label>
                <textarea
                  name="company_address"
                  value={formData.company_address}
                  onChange={handleChange}
                  placeholder="Enter full office address"
                  className="mt-1 w-full p-2.5 bg-white border border-gray-300 rounded-lg outline-none text-sm focus:ring-2 focus:ring-blue-500 h-16"
                />
              </div>

              <div className="md:col-span-2">
                <label className="text-xs font-semibold text-gray-700">About Company / Business Summary</label>
                <textarea
                  name="about_company"
                  value={formData.about_company}
                  onChange={handleChange}
                  placeholder="Brief description of products/services offered"
                  className="mt-1 w-full p-2.5 bg-white border border-gray-300 rounded-lg outline-none text-sm focus:ring-2 focus:ring-blue-500 h-16"
                />
              </div>
            </div>
          </div>

          {/* Section 3: Society Membership & Status */}
          <div className="bg-purple-50/50 p-4 rounded-xl border border-purple-100">
            <h3 className="text-xs font-bold text-purple-900 uppercase mb-3 tracking-wider">🎖️ Society Role & Status</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-gray-700">System Role</label>
                <select
                  className="mt-1 w-full p-2.5 bg-white border border-gray-300 rounded-lg outline-none text-sm focus:ring-2 focus:ring-blue-500 font-medium"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                >
                  <option value="member">Member</option>
                  <option value="CWC">CWC (Chapter Working Committee)</option>
                  <option value="Leader">Leader</option>
                  <option value="Coordinator">Coordinator</option>
                </select>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-700">Designation / Role Position</label>
                <select
                  className="mt-1 w-full p-2.5 bg-white border border-gray-300 rounded-lg outline-none text-sm focus:ring-2 focus:ring-blue-500 font-medium"
                  value={formData.sub_role}
                  onChange={(e) => setFormData({ ...formData, sub_role: e.target.value })}
                >
                  <option value="">None (General Member)</option>
                  <option value="Chairman">Chairman</option>
                  <option value="Vice Chairman">Vice Chairman</option>
                  <option value="Secretary">Secretary</option>
                  <option value="Treasurer">Treasurer</option>
                  {availableRolesList.map((r: any) => {
                    const fullTitle = r.sub_category ? `${r.role_name} - ${r.sub_category}` : r.role_name;
                    if (['Chairman', 'Vice Chairman', 'Secretary', 'Treasurer'].includes(fullTitle)) return null;
                    return (
                      <option key={r.id} value={fullTitle}>
                        {fullTitle}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-700">Account Status</label>
                <select
                  className="mt-1 w-full p-2.5 bg-white border border-gray-300 rounded-lg outline-none text-sm focus:ring-2 focus:ring-blue-500 font-medium"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  <option value="active">Active</option>
                  <option value="pending">Pending Verification</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-700">Payment / Reference Details</label>
                <input
                  type="text"
                  name="payment_details"
                  value={formData.payment_details}
                  onChange={handleChange}
                  placeholder="e.g. UPR/Transfer Ref or Cash Receipt"
                  className="mt-1 w-full p-2.5 bg-white border border-gray-300 rounded-lg outline-none text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Section 4: Security & Image */}
          <div className="bg-gray-100 p-4 rounded-xl border border-gray-200">
            <h3 className="text-xs font-bold text-gray-700 uppercase mb-3 tracking-wider">🔐 Security & Profile Image</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-gray-700">Account Password *</label>
                <div className="relative mt-1">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full p-2.5 pr-10 bg-white border border-gray-300 rounded-lg outline-none text-sm focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-2.5 text-gray-500 hover:text-gray-700 text-xs font-bold"
                  >
                    {showPassword ? "🙈 Hide" : "👁️ Show"}
                  </button>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-700">Profile Photo</label>
                <input
                  type="file"
                  onChange={handleFileChange}
                  className="mt-1 w-full p-1.5 bg-white border border-gray-300 rounded-lg text-xs"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 border-t pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 border border-gray-300 text-gray-700 font-bold text-sm rounded-lg hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2.5 bg-[#0A2451] hover:bg-blue-900 text-white font-bold text-sm rounded-lg shadow"
            >
              Save Member Details
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
interface Gallery {
  id: number;
  name: string;
  path: string;
}

interface GalleryModalProps {
  onImageSelect: (path: string) => void; // Callback function to return selected image path
  onClose: () => void;
}

const GalleryModal: React.FC<GalleryModalProps> = ({
  onImageSelect,
  onClose,
}) => {
  const [mediaData, setMediaData] = useState<Gallery[]>([]);
  const [selectedImgPath, setSelectedImgPath] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get("https://rmbbackend.rmbjaipur.com/auth/media");

        if (Array.isArray(response.data.data)) {
          setMediaData(response.data.data);
        } else {
          console.error("Response is not an array:", response.data);
        }
      } catch (error) {
        console.error("Error fetching media data:", error);
      }
    };

    fetchData();
  }, []);

  const handleImageSelect = (path: string) => {
    onImageSelect(path);
    setSelectedImgPath(path);
  };

  return (
    <div className="p-4">
      <h1 className="text-lg font-bold mb-4">Choose media from here</h1>
      {mediaData.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2 h-[340px] overflow-y-auto">
          {mediaData.map((imagedata) => (
            <div
              className={`w-full aspect-square border cursor-pointer ${
                selectedImgPath && imagedata.path === selectedImgPath
                  ? "border-2 border-blue-500 opacity-35"
                  : ""
              }`}
              key={imagedata.id}
              onClick={() => handleImageSelect(imagedata.path)}
            >
              <img
                src={`https://rmbbackend.rmbjaipur.com/${imagedata.path}`}
                alt={imagedata.name || "Media item"}
                className="object-cover w-full h-full rounded-md shadow-md"
              />
            </div>
          ))}
        </div>
      ) : (
        <p className="text-center text-gray-500">
          No media available to display.
        </p>
      )}
      <div
        className="w-[90px] h-[45px] bg-blue-500 text-white flex justify-center items-center rounded mt-2 cursor-pointer"
        onClick={onClose}
      >
        Select file
      </div>
    </div>
  );
};

export default Member;


